"use client";

import { c as _c } from "react/compiler-runtime";
import { Copy, Code2 } from "lucide-react";
import { useTheme } from "next-themes";
import { Highlight, themes } from "prism-react-renderer";
import { useState, useEffect } from "react";
import { Button } from "@/lib/components/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/lib/components/dialog";
import { toast } from "@/lib/components/use-toast";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import type { Language } from "prism-react-renderer";
import type { CSSProperties } from "react";
interface ModelCodeExampleDialogProps {
  modelId: string;
}
export function ModelCodeExampleDialog(t0) {
  const $ = _c(78);
  const {
    modelId
  } = t0;
  const {
    t
  } = useI18n();
  const [activeTab, setActiveTab] = useState("curl");
  const {
    resolvedTheme
  } = useTheme();
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setMounted(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = `curl -X POST https://api.doteb.com/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $LLM_GATEWAY_API_KEY" \\
  -d '{
  "model": "${modelId}",
  "messages": [
    {"role": "user", "content": "Hello, how are you?"}
  ]
}'`;
  let T0;
  let T1;
  let copyToClipboard;
  let currentExample;
  let t10;
  let t11;
  let t12;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== activeTab || $[3] !== modelId || $[4] !== t || $[5] !== t3) {
    const codeExamples = {
      curl: {
        label: "cURL",
        language: "bash",
        code: t3
      },
      openai: {
        label: "OpenAI SDK",
        language: "typescript",
        code: `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.LLM_GATEWAY_API_KEY, // or your API key string
  baseURL: "https://api.doteb.com/v1/"
});

const response = await client.chat.completions.create({
  model: "${modelId}",
  messages: [
    { role: "user", content: "Hello, how are you?" }
  ]
});

console.log(response.choices[0].message.content);`
      },
      "ai-sdk": {
        label: "AI SDK",
        language: "typescript",
        code: `import { createLLMGateway } from "@llmgateway/ai-sdk-provider";
import { generateText } from "ai";

const llmgateway = createLLMGateway({ apiKey: process.env.LLM_GATEWAY_API_KEY });

const { text } = await generateText({
  model: llmgateway("${modelId}"),
  prompt: "Write a vegetarian lasagna recipe for 4 people.",
});`
      }
    };
    currentExample = codeExamples[activeTab];
    if ($[19] !== t) {
      copyToClipboard = async (text, languageLabel) => {
        ;
        try {
          await navigator.clipboard.writeText(text);
          touchToast({
            title: t("public.models.codeExample.copiedTitle"),
            description: t("public.models.codeExample.copiedDescription", {
              language: languageLabel
            })
          });
        } catch (t13) {
          const err = t13;
          console.error("Failed to copy text: ", err);
          touchToast({
            title: t("public.models.codeExample.copyFailed"),
            description: t("public.models.codeExample.copyFailedDescription"),
            variant: "destructive"
          });
        }
      };
      const touchToast = _temp;
      $[19] = t;
      $[20] = copyToClipboard;
    } else {
      copyToClipboard = $[20];
    }
    T1 = Dialog;
    let t13;
    if ($[21] !== t) {
      t13 = t("public.models.codeExample.viewExamples");
      $[21] = t;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Code2 className="h-3 w-3" />;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    if ($[24] !== t13) {
      t12 = <DialogTrigger asChild={true}><Button variant="ghost" size="sm" className="h-5 w-5 p-0" type="button" aria-label={t13}>{t14}</Button></DialogTrigger>;
      $[24] = t13;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    T0 = DialogContent;
    t10 = "sm:max-w-2xl";
    let t15;
    if ($[26] !== t) {
      t15 = t("public.models.codeExample.title");
      $[26] = t;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    let t16;
    if ($[28] !== t15) {
      t16 = <DialogTitle>{t15}</DialogTitle>;
      $[28] = t15;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    let t17;
    if ($[30] !== modelId || $[31] !== t) {
      t17 = t("public.models.codeExample.description", {
        model: modelId
      });
      $[30] = modelId;
      $[31] = t;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== t17) {
      t18 = <DialogDescription>{t17}</DialogDescription>;
      $[33] = t17;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    if ($[35] !== t16 || $[36] !== t18) {
      t11 = <DialogHeader>{t16}{t18}</DialogHeader>;
      $[35] = t16;
      $[36] = t18;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    t8 = "space-y-4";
    const t19 = (Object.keys(codeExamples) as Array<"curl" | "openai" | "ai-sdk">).map(key => {
      const example = codeExamples[key];
      return <button key={key} type="button" onClick={() => setActiveTab(key)} className={cn("rounded-md px-3 py-1.5 text-xs font-medium transition-colors", activeTab === key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80")}>{example.label}</button>;
    });
    if ($[38] !== t19) {
      t9 = <div className="flex flex-wrap gap-2">{t19}</div>;
      $[38] = t19;
      $[39] = t9;
    } else {
      t9 = $[39];
    }
    t7 = "overflow-hidden rounded-md border bg-card";
    t6 = "flex items-center justify-between border-b bg-muted px-3 py-2";
    t4 = "text-xs font-medium text-muted-foreground";
    t5 = t("public.models.codeExample.example", {
      language: currentExample.label
    });
    $[2] = activeTab;
    $[3] = modelId;
    $[4] = t;
    $[5] = t3;
    $[6] = T0;
    $[7] = T1;
    $[8] = copyToClipboard;
    $[9] = currentExample;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    copyToClipboard = $[8];
    currentExample = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t13;
  if ($[40] !== t4 || $[41] !== t5) {
    t13 = <span className={t4}>{t5}</span>;
    $[40] = t4;
    $[41] = t5;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== copyToClipboard || $[44] !== currentExample.code || $[45] !== currentExample.label) {
    t14 = () => copyToClipboard(currentExample.code, currentExample.label);
    $[43] = copyToClipboard;
    $[44] = currentExample.code;
    $[45] = currentExample.label;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Copy className="mr-1 h-3 w-3" />;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t) {
    t16 = t("public.models.codeExample.copy");
    $[48] = t;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t14 || $[51] !== t16) {
    t17 = <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={t14}>{t15}{t16}</Button>;
    $[50] = t14;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t13 || $[54] !== t17 || $[55] !== t6) {
    t18 = <div className={t6}>{t13}{t17}</div>;
    $[53] = t13;
    $[54] = t17;
    $[55] = t6;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  const t19 = mounted && resolvedTheme === "dark" ? themes.dracula : themes.github;
  let t20;
  if ($[57] !== currentExample.code || $[58] !== currentExample.language || $[59] !== t19) {
    t20 = <div className="bg-background"><Highlight code={currentExample.code} language={currentExample.language} theme={t19}>{_temp2}</Highlight></div>;
    $[57] = currentExample.code;
    $[58] = currentExample.language;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== t18 || $[62] !== t20 || $[63] !== t7) {
    t21 = <div className={t7}>{t18}{t20}</div>;
    $[61] = t18;
    $[62] = t20;
    $[63] = t7;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== t21 || $[66] !== t8 || $[67] !== t9) {
    t22 = <div className={t8}>{t9}{t21}</div>;
    $[65] = t21;
    $[66] = t8;
    $[67] = t9;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== T0 || $[70] !== t10 || $[71] !== t11 || $[72] !== t22) {
    t23 = <T0 className={t10}>{t11}{t22}</T0>;
    $[69] = T0;
    $[70] = t10;
    $[71] = t11;
    $[72] = t22;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  let t24;
  if ($[74] !== T1 || $[75] !== t12 || $[76] !== t23) {
    t24 = <T1>{t12}{t23}</T1>;
    $[74] = T1;
    $[75] = t12;
    $[76] = t23;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  return t24;
}
function _temp2(t0) {
  const {
    className,
    style,
    tokens,
    getLineProps,
    getTokenProps
  } = t0;
  return <pre className={cn("max-h-80 overflow-auto p-4 text-xs leading-relaxed font-mono", className)} style={style}>{tokens.map((line, i) => {
      const lineProps = getLineProps({
        line
      });
      return <div key={i} {...lineProps}>{line.map((token, key_0) => {
          const tokenProps = getTokenProps({
            token
          });
          return <span key={key_0} {...tokenProps} />;
        })}</div>;
    })}</pre>;
}
function _temp(args) {
  toast({
    duration: 3000,
    ...args
  });
}